/*
 * ------------------------------------------------------------
 * Storefront Variant Resolver
 * ------------------------------------------------------------
 * Resolves a product variant from the currently-selected options
 * using the Shopify Storefront API (GraphQL).
 *
 * WHY: Liquid's `product.variants` object is capped at 250 items,
 * and this theme builds its client-side variant lookup from that
 * truncated JSON (see VariantSelects.getVariantData in global.js).
 * Any option combination that maps to variant #251+ therefore can't
 * be found, so price / availability / add-to-cart break.
 *
 * The Storefront API's `variantBySelectedOptions` query has NO
 * 250-variant limit, so it returns the exact variant for the chosen
 * combination. global.js calls resolve() only as a fallback, when the
 * combination is NOT in the embedded first-250 JSON. Products with
 * <= 250 variants are unaffected (they never reach this code).
 *
 * Config is injected by layout/theme.liquid as window.StorefrontVariantConfig.
 * If no token is configured, `enabled` is false and the theme keeps its
 * existing behavior (nothing gets worse).
 * ------------------------------------------------------------
 */
(function () {
  var cfg = window.StorefrontVariantConfig || {};
  var TOKEN = cfg.token || '';
  var DOMAIN = cfg.domain || (window.Shopify && window.Shopify.shop) || '';
  var API_VERSION = cfg.apiVersion || '2024-10';

  var QUERY = [
    'query resolveVariant($handle: String!, $opts: [SelectedOptionInput!]!) {',
    '  product(handle: $handle) {',
    '    variantBySelectedOptions(selectedOptions: $opts) {',
    '      id',
    '      sku',
    '      availableForSale',
    '      price { amount currencyCode }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  var cache = {};    // key -> normalized variant (or null for "no such combination")
  var inflight = {}; // key -> Promise (de-dupes concurrent identical lookups)

  function handleFromUrl(url) {
    if (!url) return '';
    return String(url).split('?')[0].replace(/\/+$/, '').split('/').pop();
  }

  // Build the {name, value} list from the picker's <select> and checked radio inputs.
  // Selects render as name="options[Wood Species]"; the special wood option renders
  // as radios with name="Wood Type" / "Wood & Finish". Both are handled here so we
  // never have to edit individual product sections.
  function readSelectedOptions(root) {
    var opts = [];
    var seen = {};
    function add(name, value) {
      if (!name || seen.hasOwnProperty(name)) return;
      seen[name] = true;
      opts.push({ name: name, value: value });
    }

    root.querySelectorAll('select[name^="options["]').forEach(function (sel) {
      var m = sel.name.match(/options\[(.+)\]/);
      if (m) add(m[1], sel.value);
    });

    root.querySelectorAll('input[type="radio"]:checked').forEach(function (r) {
      var m = r.name.match(/options\[(.+)\]/);
      add(m ? m[1] : r.name, r.value);
    });

    return opts;
  }

  // Shape the Storefront result to match what global.js's downstream methods expect
  // (updateVariantInput, renderProductInfo, updateURL, etc. read .id / .sku / .price / .available).
  function normalize(v) {
    if (!v || !v.id) return null;
    var idMatch = String(v.id).match(/(\d+)\s*$/); // gid://shopify/ProductVariant/123 -> 123
    var cents = 0;
    if (v.price && v.price.amount != null) {
      cents = Math.round(parseFloat(v.price.amount) * 100);
    }
    return {
      id: idMatch ? parseInt(idMatch[1], 10) : v.id,
      sku: v.sku || '',
      available: !!v.availableForSale,
      // Made-to-order default. Server-rendered price/stock (renderProductInfo) is authoritative;
      // this only feeds the theme's "In Stock vs Made to Order" label fallback.
      inventory_quantity: 0,
      price: cents,
      featured_media: null // image swap is skipped for 250+ variants (gallery still works)
    };
  }

  function resolve(root) {
    if (!TOKEN || !DOMAIN || !root) return Promise.resolve(null);

    var handle = root.dataset.handle || handleFromUrl(root.dataset.url);
    if (!handle) return Promise.resolve(null);

    var opts = readSelectedOptions(root);
    if (!opts.length) return Promise.resolve(null);

    var key = handle + '::' + opts.map(function (o) { return o.name + '=' + o.value; }).sort().join('&');
    if (cache.hasOwnProperty(key)) return Promise.resolve(cache[key]);
    if (inflight[key]) return inflight[key];

    var endpoint = 'https://' + DOMAIN + '/api/' + API_VERSION + '/graphql.json';
    var p = fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Shopify-Storefront-Access-Token': TOKEN
      },
      body: JSON.stringify({ query: QUERY, variables: { handle: handle, opts: opts } })
    })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        if (json && json.errors && window.console) {
          console.warn('[StorefrontVariantResolver] GraphQL errors:', json.errors);
        }
        var v = json && json.data && json.data.product && json.data.product.variantBySelectedOptions;
        var norm = normalize(v);
        cache[key] = norm;
        delete inflight[key];
        return norm;
      })
      .catch(function (e) {
        delete inflight[key];
        if (window.console) console.warn('[StorefrontVariantResolver] request failed:', e);
        return null;
      });

    inflight[key] = p;
    return p;
  }

  window.StorefrontVariantResolver = {
    get enabled() { return !!(TOKEN && DOMAIN); },
    resolve: resolve,
    readSelectedOptions: readSelectedOptions
  };
})();
